'use client';

import {
  memo,
  useCallback,
  useState,
  type ComponentProps,
  type Ref,
} from 'react';
import { useJobSearchSuggestions } from '@/services/jobs/use-job-search-suggestions';
import { JobFilterList } from './job-filter-list';
import { JobSearchHero, type JobSearchInputHandle } from './job-search-hero';
import { JobSearchToolbar } from './job-search-toolbar';

type JobSearchControlsProps = Omit<
  ComponentProps<typeof JobFilterList>,
  | 'isSuggestionLoading'
  | 'suggestionKeywords'
  | 'onPinnedSearchVisibilityChange'
> & {
  authSessionVersion: number;
  hasSearchError: boolean;
  isAuthenticated: boolean;
  queryInputRef: Ref<JobSearchInputHandle>;
};

// Draft input and suggestion responses must not rerender the results or SEO tree.
export const JobSearchControls = memo(function JobSearchControls({
  authSessionVersion,
  hasSearchError,
  isAuthenticated,
  onQueryChange,
  onSearchSubmit,
  query,
  queryInputRef,
  ...filterProps
}: JobSearchControlsProps) {
  const [draftQuery, setDraftQuery] = useState(query);
  const [previousQuery, setPreviousQuery] = useState(query);
  const [isPinnedSearchVisible, setIsPinnedSearchVisible] = useState(false);
  const [suggestionsActivated, setSuggestionsActivated] = useState(false);

  // Synchronize committed searches and the empty-state reset before rendering.
  if (previousQuery !== query) {
    setPreviousQuery(query);
    setDraftQuery(query);
  }

  const { isLoading: isSuggestionLoading, keywords: suggestionKeywords } =
    useJobSearchSuggestions(
      draftQuery,
      isAuthenticated && suggestionsActivated,
      authSessionVersion
    );
  const handleQueryChange = useCallback(
    (value: string) => {
      setDraftQuery(value);
      onQueryChange(value);
    },
    [onQueryChange]
  );
  const activateSuggestions = useCallback(
    () => setSuggestionsActivated(true),
    []
  );

  return (
    <JobSearchToolbar>
      <JobSearchHero
        key={
          isPinnedSearchVisible
            ? 'primary-search-disabled'
            : 'primary-search-enabled'
        }
        isSuggestionLoading={isSuggestionLoading}
        isSuggestionsEnabled={!isPinnedSearchVisible}
        query={draftQuery}
        isLoading={filterProps.isSearching}
        onQueryChange={handleQueryChange}
        onSuggestionsActivate={activateSuggestions}
        onSubmit={onSearchSubmit}
        queryInputRef={queryInputRef}
        suggestionKeywords={suggestionKeywords}
      />
      {!hasSearchError ? (
        <JobFilterList
          {...filterProps}
          isSuggestionLoading={isSuggestionLoading}
          onPinnedSearchVisibilityChange={setIsPinnedSearchVisible}
          onQueryChange={handleQueryChange}
          onSuggestionsActivate={activateSuggestions}
          onSearchSubmit={onSearchSubmit}
          query={draftQuery}
          suggestionKeywords={suggestionKeywords}
        />
      ) : null}
    </JobSearchToolbar>
  );
});
